import { formatDistance } from '@/helpers/DistanceHelper.js';
import MapMathHelper from "./MapMathHelper.js";
import Vector from "./Vector.js";
import store from "@/store.js";
import i18n from "@vue-components/plugins/I18nPlugin.js";
import { tr, getShortCodeFromFull } from '@/../vue-components/helpers/I18nPluginHelper.js';

/**
 * Instructions at every important point of the NavPath
 */
export default class MapInstructions {
    constructor(index, previousItem, currentItem, nextItem) {
        this.index = index;
        this.previousItem = previousItem;
        this.currentItem = currentItem;
        this.nextItem = nextItem;
        // set later
        this.nextInstruction = null;
        // in case with merged multiple links
        this.links = [];

        // TODO refactor icon into glyph
        this.icon = null;
        const optionalInstructionsItem = store.state.mapEditor.map.integrations.isImages360Enabled() ? currentItem : nextItem;
        this.disableDirections = optionalInstructionsItem?.instructionDisplay === `replace`;
        if (!this.disableDirections) {
            this.instructions = tr(optionalInstructionsItem?.instructions);
            this.instructionGlyph = optionalInstructionsItem?.instructionGlyph;
        } else {
            this.description = tr(optionalInstructionsItem?.instructions);
            this.icon = optionalInstructionsItem?.instructionGlyph;
        }
        this.allowMerge = true;

        // console.log(store.state);
        this.fromFloor = store.state.mapEditor.map.getFloorById(this.currentItem.floorId);
        this.toFloor = this.fromFloor;

        if (!this.previousItem) {
            this.previousVector = new Vector(0, -1);
            if (this.currentItem.angle < 0 || this.currentItem.angle > 0) {
                this.previousVector.fromAngle(this.currentItem.angle - 180);
            }
            this.distance = 0;
        } else {
            this.previousVector = new Vector(this.previousItem.x - this.currentItem.x, this.currentItem.y - this.previousItem.y);

            this.link = currentItem.links.find(l => l.isLinkBetween(this.currentItem, this.previousItem));
            this.links.push(this.link);
            // replace fromFloor based on the link floor, since stairs and elevators are placed on the Ground Floor, but can be accessed from many floors
            this.fromFloor = store.state.mapEditor.map.getFloorById(this.link.floorId)
            this.distance = Math.round(this.link.distance);
        }

        if (this.nextItem) {
            this.nextVector = new Vector(this.currentItem.x - this.nextItem.x, this.nextItem.y - this.currentItem.y);
            this.nextLink = currentItem.links.find(l => l.isLinkBetween(this.currentItem, this.nextItem));

            if (this.nextLink) {
                // replace fromFloor based on the link floor, since stairs and elevators are placed on the Ground Floor, but can be accessed from many floors
                this.toFloor = store.state.mapEditor.map.getFloorById(this.nextLink.floorId);
            } else {
                this.toFloor = this.fromFloor;
            }
        } else {
            this.nextVector = new Vector(0, -1);
        }

        // invert y to get correct angle since coordinates are from top to bottom
        this.previousVector.invertY();
        this.nextVector.invertY();

        this.fromAngle = this.previousVector.getAngle();
        this.toAngle = this.nextVector.getAngle();

        if (this.previousItem && this.currentItem?.linkedMapItemsOffsets) {
            const currentToPreviousOffset = this.currentItem.linkedMapItemsOffsets[this.previousItem.id];
            const isPinned = currentToPreviousOffset?.isPinned;
            const fromAngleOffset = currentToPreviousOffset?.x;

            if (isPinned && fromAngleOffset != null) {
                this.isFromHotspotAngleAbsolute = !!isPinned;
                this.fromHotspotAngle = fromAngleOffset;
            }
        }
        if (this.nextItem && this.currentItem?.linkedMapItemsOffsets) {
            const isPinned = this.currentItem.linkedMapItemsOffsets[this.nextItem.id]?.isPinned;
            const toAngleOffset = this.currentItem.linkedMapItemsOffsets[this.nextItem.id]?.x;


            if (isPinned && toAngleOffset != null) {
                this.isToAngleAbsolute = !!isPinned;
                this.toAngle = toAngleOffset;
            }
        }
        this.deltaAngle = this.previousVector.getDiffAngle(this.nextVector);

        this.image360 = this.currentItem.image360 || null;
        this.offsetAngle = this.currentItem.offsetAngle || 0;

        // Create multiple issues, + we can't change the 360 image without also updating the offset
        // if (this.index === 0 && !this.image360 && this.nextItem?.image360) {
        //     this.image360 = this.nextItem.image360;
        // }

        this.image = this.currentItem.image || null;

        // map should only ever care about location picture and not the directory itself
        // if (!this.image && this.currentItem.cmsItem) {
        //     this.image = this.currentItem.cmsItem.getPreviewImage();
        // }

        // called later by setNextInstruction
        this.updateInstructions();

        // console.log(`${currentItem.index} -> ${this.deltaAngle}° - ${this.direction}`);
    }

    isVisible() {
        if (this.index === 0 && this.nextLink) {
            return this.nextLink.visible && !this.nextLink.optional;
        } else if (this.link) {
            return this.link.visible && !this.link.optional;
        }
        return true;
    }

    setArrival(end) {
        this.direction = `arrived`;
        this.icon = `fa-flag-checkered`;
        if (!this.disableDirections) {
            this.description = tr(`map.instructions.arrivedAt`);
            this.nextLocation = end.getFullName();
            this.addInstructions();
        }
    }

    setNextInstruction(nextInstruction) {
        this.nextInstruction = nextInstruction;
        this.updateInstructions();
    }

    updateInstructions() {
        this.currentLocation = this.getItemLocation(this.currentItem);

        if (this.disableDirections)
            return;

        this.showDistance = true;
        this.description = ``;
        this.nextLocation = ``;
        this.action = tr(`map.instructions.walk`);
        this.direction = MapMathHelper.getAngleDirection(this.deltaAngle);

        if (this.index === 0) {
            this.icon = `fa-map-pin`;
        } else {
            switch (this.direction) {
                case `right`:
                    this.description = tr(`map.instructions.onRight`);
                    this.icon = `fak fa-turn-right fa-xl`;
                    break;
                case `light-right`:
                    this.description = tr(`map.instructions.onRight`);
                    this.icon = `fak fa-up-right fa-xl`;
                    break;
                case `left`:
                    this.description = tr(`map.instructions.onLeft`);
                    this.icon = `fak fa-turn-left fa-xl`;
                    break;
                case `light-left`:
                    this.description = tr(`map.instructions.onLeft`);
                    this.icon = `fak fa-up-left fa-xl`;
                    break;
                case `forward`:
                    this.description = tr(`map.instructions.straight`);
                    this.icon = `fak fa-up-long fa-xl`;
                    break;
                case `backward-left`:
                case `backward-right`:
                    this.description = tr(`map.instructions.backward`);
                    this.icon = `fak fa-turn-left-down fa-xl`;
                    break;
            }
        }

        if (this.currentItem?.type === `elevator`) {
            this.currentLocation = tr(`map.instructions.inElevator`);
        } else if (this.previousItem?.type === `stairs`) {
            this.currentLocation = tr(`map.instructions.inStaircase`);
        }

        if (this.toFloor !== this.fromFloor) {
            if (this.toFloor.level > this.fromFloor.level) {
                this.direction = `up`;
                if (this.currentItem.type === `elevator`) {
                    this.icon = `fa-elevator`;
                } else if (this.currentItem.type === `stairs`) {
                    this.icon = `fa-stairs`;
                } else {
                    this.icon = `fak fa-up-from-dotted-line fa-xl`;
                }
                // Note: i18n.t() is still needed here for interpolation support with {floor}
                this.description = i18n.t(`map.instructions.goUpTo`, { floor: this.toFloor.getName() });
                this.showDistance = false;
                return;
            } else {
                this.direction = `down`;
                if (this.currentItem.type === `elevator`) {
                    this.icon = `fa-elevator`;
                } else if (this.currentItem.type === `stairs`) {
                    this.icon = `fa-stairs`;
                } else {
                    this.icon = `fa-down-from-dotted-line`;
                }
                // Note: i18n.t() is still needed here for interpolation support with {floor}
                this.description = i18n.t(`map.instructions.goDownTo`, { floor: this.toFloor.getName() });
                this.showDistance = false;
                return;
            }
        } else {
            if (this.index === 0) {
                // first item
                this.description = tr(`map.instructions.goToStartPoint`);

                const location = this.getItemLocation(this.currentItem, false, false);
                if (location?.trim()) {
                    this.description += ` ${location}`;
                }
                if (!this.image360) {
                    this.showDistance = false;
                    return;
                }

                if (!this.currentItem.angle) {
                    this.description = tr(`map.instructions.followingArrow`);
                }
            } else if (this.nextItem) {
                if (this.nextItem.type === `elevator`) {
                    if (this.distance < 6) {
                        this.showDistance = false;
                        this.description = tr(`map.instructions.takeElevator`) + ` ` + this.description;
                    } else {
                        this.description += `,`;
                        this.nextLocation += tr(`map.instructions.takeElevatorInline`);
                    }
                    return;
                }
            }
        }
        this.addInstructions();
    }

    addInstructions() {
        if (this.nextInstruction?.currentItem.getLocationDescription()) {
            this.description = this.description.toLowerCase();

            let location = this.getItemLocation(this.nextInstruction.currentItem, false, true);
            this.nextLocation = location;
        }

        // if (!tr(this.nextItem?.instructions)) {
        //     if (this.nextItem?.type === `door`) {
        //         this.description += tr(`map.instructions.throughDoor`);
        //     }
        // }
    }

    getItemLocation(item, addPronoun = true, addUntilPrefix = false) {
        let location = item.getLocationDescription();
        location = tr(location);
        if (!location) {
            return ``;
        }
        if (this.isFrench()) {
            if (addPronoun || addUntilPrefix) {
                const pronoun = this.getPronoun(location);
                location = location.replace(/^(le\s)?(les\s)?/i, ``);
                if (addUntilPrefix) {
                    location = `${tr(`map.instructions.untilPrefix`)}${pronoun.toLowerCase()} ${this.lowercaseStartLetter(location)}`;
                } else {
                    location = `${pronoun} ${this.lowercaseStartLetter(location)}`;
                }
            }
        } else {
            if (addUntilPrefix) {
                location = `${tr(`map.instructions.untilPrefix`)} ${this.lowercaseStartLetter(location)}`;
            } else if (addPronoun) {
                location = `At ${this.lowercaseStartLetter(location)}`;
            }
        }
        const address = item.getAddress();
        if (address) {
            location += ` ${address}`;
        }
        return location;
    }

    lowercaseStartLetter(text) {
        if (text?.length > 0) {
            return text.charAt(0).toLowerCase() + text.slice(1);
        } else {
            return text;
        }
    }

    getPronoun(location) {
        if (this.isFrench()) {
            if (location.toLowerCase().startsWith(`les`)) {
                return `Aux`;
            } else if (location.toLowerCase().startsWith(`le`)) {
                return `Au`;
            } else {
                return `À`;
            }
        } else {
            return `At`;
        }
    }

    canMerge(prevInstruction) {
        if (this.toFloor.id !== prevInstruction.fromFloor.id || !prevInstruction.allowMerge)
            return false;

        if (prevInstruction.direction !== `forward` && prevInstruction.direction !== `backward`) {
            if (store.state.mapEditor.map.integrations.isImages360Enabled()) {
                return false;
            }

            // With simple maps, we can merge only one instruction that doesn't go forward,
            // ex: to simplify when a corridor brings to a door on the side
            return this.distance < 4;
        } else {
            return prevInstruction.distance + this.distance < 50;
        }
    }

    mergeWith(prevInstruction) {
        // We can merge only one instruction that doesn't go forward,
        // ex: to simplify when a corridor brings to a door on the side
        if (prevInstruction.direction !== `forward`) {
            this.allowMerge = false;
        }

        // console.log(`merging ${navInstruction.index} in ${this.index}`);
        // this.index = navInstruction.index;
        this.distance += prevInstruction.distance;
        this.fromAngle = prevInstruction.fromAngle;
        // this.toAngle = navInstruction.toAngle;
        this.deltaAngle += prevInstruction.deltaAngle;
        this.originalItem = prevInstruction.currentItem;
        if (prevInstruction.link) {
            this.links = prevInstruction.links.concat(this.links);
        }
        this.previousItem = prevInstruction.previousItem;
        if (this.nextItem) {
            if (!this.instructions && this.nextItem.instructions) {
                this.instructions = tr(prevInstruction.nextItem.instructions);
            }
            if (!this.instructionGlyph) {
                this.instructionGlyph = prevInstruction.nextItem.instructionGlyph;
            }
        }
        if (prevInstruction.image) {
            this.image = prevInstruction.image;
        }
        this.updateInstructions();
    }

    getTextToSpeech() {
        let textToSpeech = ``;
        if (this.showDistance && this.nextInstruction?.distance > 0) {
            const distanceText = formatDistance(this.nextInstruction.distance, { style: `spaced` });
            textToSpeech = `${this.action} ${distanceText}`;
        }

        textToSpeech = `${textToSpeech} ${this.description}`;

        if (this.nextLocation) {
            textToSpeech = `${textToSpeech} ${this.nextLocation}`;
        }

        if (this.instructions) {
            textToSpeech += `. ${this.instructions}`;
        }

        return textToSpeech;
    }

    isFrench() {
        const locale = (i18n && i18n.locale) || `en`;
        const shortCode = getShortCodeFromFull(locale) || locale;
        return shortCode === `fr`;
    }

    getMarkerDistance(markerLocation) {
        return Math.sqrt((markerLocation.x - this.previousItem.x)**2 + (markerLocation.y - this.previousItem.y)**2);
    }

    /**
     * Approximate the distance walked compared to the beginning of the instruction
     */
    getWalkedProgress(markerLocation) {
        const fromDistance = this.getMarkerDistance(markerLocation);
        const totalDistance = Math.sqrt((this.currentItem.x - this.previousItem.x)**2 + (this.currentItem.y - this.previousItem.y)**2);
        return fromDistance / totalDistance;
    }
}
